import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Video, CreditCard, Check, X, Clock, FileText, Search, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  confirmed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-700",
};

const docStatusColors = {
  none: "bg-stone-100 text-stone-500",
  uploaded: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

export default function AdminPaymentsPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  const [tab, setTab] = useState("zoom");
  const [zoomRegs, setZoomRegs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!loading && (!isAuthenticated || user?.role !== "admin")) navigate("/");
  }, [isAuthenticated, user, loading, navigate]);

  useEffect(() => {
    if (token) { fetchZoomRegs(); fetchPayments(); }
  }, [token]);

  const fetchZoomRegs = async () => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/zoom-date-registrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { const d = await res.json(); setZoomRegs(d.registrations || []); }
    } catch { /* */ }
    finally { setIsLoading(false); }
  };

  const fetchPayments = async () => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/payment-transactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { const d = await res.json(); setPayments(d.transactions || []); }
    } catch { /* */ }
  };

  const updateZoomReg = async (regId, field, value) => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/zoom-date-registrations/${regId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) fetchZoomRegs();
    } catch { /* */ }
  };

  const filteredZoom = zoomRegs.filter(r => {
    if (searchQuery && !r.username?.includes(searchQuery) && !r.email?.includes(searchQuery)) return false;
    if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
    return true;
  });

  const filteredPayments = payments.filter(r => {
    if (searchQuery && !r.email?.includes(searchQuery)) return false;
    if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
    return true;
  });

  if (loading) return null;

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-payments-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 hover:bg-stone-200 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">결제 & 이벤트 관리</h1>
            <p className="text-sm text-stone-500">줌데이트 신청, 결제 내역 관리</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-stone-200 mb-6 w-fit">
          <button
            onClick={() => { setTab("zoom"); setStatusFilter("all"); setSearchQuery(""); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === "zoom" ? "bg-burgundy-500 text-white" : "text-stone-600 hover:bg-stone-50"
            }`}
          >
            <Video className="w-4 h-4" />
            줌데이트 신청 ({zoomRegs.length})
          </button>
          <button
            onClick={() => { setTab("payments"); setStatusFilter("all"); setSearchQuery(""); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === "payments" ? "bg-burgundy-500 text-white" : "text-stone-600 hover:bg-stone-50"
            }`}
          >
            <CreditCard className="w-4 h-4" />
            멤버십 결제 ({payments.length})
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="이름 또는 이메일 검색"
              className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-burgundy-300"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white focus:outline-none"
          >
            <option value="all">전체 상태</option>
            <option value="pending">대기중</option>
            <option value="paid">결제완료</option>
            <option value="confirmed">확정</option>
            <option value="cancelled">취소</option>
          </select>
        </div>

        {/* Zoom Date Registrations Tab */}
        {tab === "zoom" && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
            {isLoading ? (
              <p className="text-center py-12 text-stone-400">로딩중...</p>
            ) : filteredZoom.length === 0 ? (
              <p className="text-center py-12 text-stone-400">등록된 줌데이트 신청이 없습니다</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">신청자</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">이벤트</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">신청일</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">결제상태</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">서류인증</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredZoom.map((reg) => (
                      <tr key={reg.id} className="hover:bg-stone-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-stone-800">{reg.username}</p>
                            <p className="text-xs text-stone-400">{reg.email || "—"}</p>
                            <p className="text-xs text-stone-400">{reg.gender === "male" ? "남" : "여"} · {reg.birth_year || "—"}년생</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-stone-700">{reg.event_id}</p>
                          <p className="text-xs text-stone-400">{reg.membership_type}</p>
                        </td>
                        <td className="px-4 py-3 text-stone-600 text-xs">
                          {new Date(reg.registered_at).toLocaleDateString("ko-KR")}
                          <br />
                          {new Date(reg.registered_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={reg.payment_status || "pending"}
                            onChange={(e) => updateZoomReg(reg.id, "payment_status", e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full border-none focus:outline-none cursor-pointer ${statusColors[reg.payment_status] || statusColors.pending}`}
                          >
                            <option value="pending">대기중</option>
                            <option value="paid">결제완료</option>
                            <option value="confirmed">확정</option>
                            <option value="cancelled">취소</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          {reg.document_verified ? (
                            <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1 w-fit">
                              <Check className="w-3 h-3" /> 인증완료
                            </span>
                          ) : (
                            <select
                              value={reg.doc_status || "none"}
                              onChange={(e) => updateZoomReg(reg.id, "doc_status", e.target.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-full border-none focus:outline-none cursor-pointer ${docStatusColors[reg.doc_status] || docStatusColors.none}`}
                            >
                              <option value="none">미제출</option>
                              <option value="uploaded">업로드됨</option>
                              <option value="approved">승인</option>
                              <option value="rejected">거절</option>
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder="메모"
                            defaultValue={reg.admin_note || ""}
                            onBlur={(e) => { if (e.target.value !== (reg.admin_note || "")) updateZoomReg(reg.id, "admin_note", e.target.value); }}
                            className="text-xs border border-stone-200 rounded-lg px-2 py-1 w-28 focus:outline-none focus:ring-1 focus:ring-burgundy-300"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {tab === "payments" && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
            {payments.length === 0 ? (
              <p className="text-center py-12 text-stone-400">결제 내역이 없습니다</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">이메일</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">멤버십</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">결제주기</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">금액</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">결제상태</th>
                      <th className="text-left px-4 py-3 font-medium text-stone-500">결제일</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredPayments.map((txn) => (
                      <tr key={txn.id} className="hover:bg-stone-50">
                        <td className="px-4 py-3 text-stone-700">{txn.email}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium bg-burgundy-100 text-burgundy-700 px-2 py-0.5 rounded-full">{txn.membership_type}</span>
                        </td>
                        <td className="px-4 py-3 text-stone-600">{txn.billing_cycle === "yearly" ? "연간" : "월간"}</td>
                        <td className="px-4 py-3 font-medium text-stone-800">{txn.currency?.toUpperCase() || "USD"} ${txn.amount}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[txn.payment_status] || statusColors.pending}`}>
                            {txn.payment_status === "paid" ? "결제완료" : txn.payment_status === "pending" ? "대기중" : txn.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-stone-500">
                          {new Date(txn.created_at).toLocaleDateString("ko-KR")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
